import Alert from '@mui/material/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import React, { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  Box,
  Stack,
  Theme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { TemplateUsageInstructions } from 'components/models/FormTemplate';
import CustomTooltipAsPopover, {
  DefaultTooltipButton
} from 'components/pages/common/CustomTooltipAsPopover';
import {
  getLocalStorageItem,
  LOCALSTORAGE_KEYS,
  setLocalStorageItem
} from 'components/utils/local_storage';
import { findLastIndex } from 'components/utils/array';
import {
  completeWogaaTransactionalService,
  startWogaaTransactionalService
} from 'components/utils/wogaa';
import {
  newElement,
  UnionElement
} from '../../../../form_elements/BaseElement';
import {
  cloneElement,
  deleteElement,
  findElement,
  insertElement,
  parentElementOf,
  reorderElement,
  updateElement
} from '../../../../form_elements/RootElement';
import { PublishShareModal } from './publishShareModal/PublishShareModal';
import AudienceMetadata from '../../../../models/AudienceMetadata';
import { isEmpty, isNotEmpty } from '../../../../utils/equality';
import { InstructionsID } from '../../../../form_elements/instructions/Instructions';
import {
  SectionElement,
  SectionElementType
} from '../../../../form_elements/section/SectionElement';
import { TitleID } from '../../../../form_elements/title/Title';
import FormProps, {
  FormConfigProps,
  FormErrors,
  FormUpdateResponseData,
  UpdateScheduleProps,
  UpdateScheduleResponse
} from '../../../../models/FormProps';
import { SaveStatus } from './ContentContainer';
import Editor from './editor/Editor';
import Form from './form/Form';
import EditQuestionsIframe, {
  EditQuestionsIframeHandle
} from './edit_questions_iframe/EditQuestionsIframe';

interface Props {
  form: FormProps;
  formConfig: FormConfigProps;
  template: TemplateUsageInstructions;
  formErrors: FormErrors;
  saveStatus: SaveStatus;
  metadata: AudienceMetadata;
  hasAdditionalMetadata: boolean;
  onAddMetadata: () => void;
  metadataValuesWithHeaderUrl: string;
  previewUrl: string;
  formUrl: string;
  formsUrl: string;
  updateFormImageUrl: string;
  createAnnouncementOnPgUrl: string;
  onChangeForm: (form: FormProps) => void;
  onUpdateFormConfig: (
    data: FormConfigProps
  ) => Promise<FormUpdateResponseData | null>;
  onUpdateSchedule: (
    data: UpdateScheduleProps
  ) => Promise<UpdateScheduleResponse | null>;
  viewOnly: boolean;
  isShareableToPG: boolean;
  formPublishErrors: Array<string>;
  isParentForm: boolean;
  parentFormFaqUrl: string;
  wogaaPublishFormTrackingId: string;
  editQuestionIframeSource: string;
  useV2Editor: boolean;
  toggleEditorVersion: (value: 'v1' | 'v2') => void;
  iframeRef: RefObject<EditQuestionsIframeHandle>;
  onIframeVersionUpdate: (version: number) => void;
  logSwitchReason: (reason: string) => void;
  enforceV2Editor?: boolean;
}

const TOOLTIP_ID = LOCALSTORAGE_KEYS.EDIT_QUESTION_PUBLISH_SHARE_MODAL_TOOLTIP;
const NEW_UI_DIALOG_ID = LOCALSTORAGE_KEYS.EDIT_QUESTION_NEW_UI_DIALOG;

export default function Content(props: Props): JSX.Element {
  const {
    form,
    formConfig,
    template: templateUsageInstructions,
    formErrors,
    saveStatus,
    metadata,
    onAddMetadata,
    hasAdditionalMetadata,
    metadataValuesWithHeaderUrl,
    previewUrl,
    formUrl,
    formsUrl,
    updateFormImageUrl,
    createAnnouncementOnPgUrl,
    onChangeForm,
    onUpdateFormConfig,
    onUpdateSchedule,
    viewOnly,
    isShareableToPG,
    formPublishErrors,
    isParentForm,
    parentFormFaqUrl,
    wogaaPublishFormTrackingId,
    editQuestionIframeSource,
    useV2Editor,
    toggleEditorVersion,
    iframeRef,
    onIframeVersionUpdate,
    logSwitchReason,
    enforceV2Editor
  } = props;

  const [formState, setFormState] = useState<FormProps>(form);
  const [selectedElementID, setSelectedElementID] = useState<string>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [switchReason, setSwitchReason] = useState('');
  const [showNewUIDialog, setShowNewUIDialog] = useState(false);
  const [canPublishForm, setCanPublishForm] = useState(true);

  const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const tooltipOpen = Boolean(tooltipAnchorEl);
  const tooltipId = tooltipOpen ? TOOLTIP_ID : undefined;

  const isMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const shouldDisplayWogaaWidget = formState.body.children.length > 0;

  useEffect(() => onChangeForm(formState), [formState]);

  useEffect(() => {
    // Show dialog only if user hasn't seen it before and we're on the v2 editor
    if (useV2Editor && getLocalStorageItem(NEW_UI_DIALOG_ID) !== '1') {
      setShowNewUIDialog(true);
    }
  }, [useV2Editor]);

  // We store a ref to the ResizeObserver to allow clean up (see below) and prevent memory leaks.
  const selectedElementResizeObserverRef = useRef<ResizeObserver>(null);

  // We use a ref callback to automatically scroll the selected element into view
  // when it is selected (whether by the user or by adding a new question).
  const selectedElementRefCallback = useCallback((element: HTMLElement) => {
    // Nothing is selected.
    if (element === null) {
      // Clean up the resize observer connected to the previous selected element (if any).
      selectedElementResizeObserverRef.current?.disconnect();
      selectedElementResizeObserverRef.current = null;
      return;
    }

    const scrollOptions: ScrollIntoViewOptions = {
      behavior: 'smooth',
      block: 'nearest' // Only scroll if the element is not in view.
    };
    element.scrollIntoView(scrollOptions);

    // Set up an observer to scroll the selected element into view whenever its dimensions change.
    // This is particularly useful when adding new images, because `element.scrollIntoView()` above is called
    // before the default image loads, i.e. when the image loads, the image element's height increases,
    // causing the image to appear partially out of view.
    const observer = new ResizeObserver(() => {
      element.scrollIntoView(scrollOptions);
    });
    observer.observe(element);
    selectedElementResizeObserverRef.current = observer;
  }, []);

  const handleOpenShareModal = () => {
    if (shouldDisplayWogaaWidget) {
      startWogaaTransactionalService(wogaaPublishFormTrackingId, form.id);
    }
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    if (shouldDisplayWogaaWidget) {
      completeWogaaTransactionalService(wogaaPublishFormTrackingId, form.id);
    }
    setShowShareModal(false);
  };

  const handleVersionSwitch = () => {
    if (useV2Editor) {
      // Switching back to old version - show dialog
      setShowSwitchDialog(true);
    } else {
      // Switching to new version - no dialog needed
      toggleEditorVersion('v2');
    }
  };

  const handleCloseSwitchDialog = () => {
    setShowSwitchDialog(false);
    setSwitchReason('');
  };

  const handleConfirmSwitch = () => {
    logSwitchReason(switchReason);
    toggleEditorVersion('v1');
    handleCloseSwitchDialog();
  };

  const handleCloseNewUIDialog = () => {
    setLocalStorageItem(NEW_UI_DIALOG_ID, '1');
    setShowNewUIDialog(false);
  };

  return (
    <>
      <main className={`manage-forms-edit-page-content manage-forms-edit-questions-page-content${useV2Editor ? ' v2-editor-active' : ''}`}>
        {!(enforceV2Editor && useV2Editor) && renderHeader()}

        {showShareModal && (
          <PublishShareModal
            formName={formState.title}
            formUrl={formUrl}
            createAnnouncementOnPgUrl={createAnnouncementOnPgUrl}
            formConfig={formConfig}
            onUpdateFormConfig={onUpdateFormConfig}
            onUpdateSchedule={onUpdateSchedule}
            onCloseModal={handleCloseShareModal}
            viewOnly={viewOnly}
            isShareableToPG={isShareableToPG}
            formPublishErrors={formPublishErrors}
            isParentForm={isParentForm}
            parentFormFaqUrl={parentFormFaqUrl}
          />
        )}

        <Dialog
          open={showSwitchDialog}
          onClose={handleCloseSwitchDialog}
          maxWidth="xs"
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutorenewIcon />
            Switch to old version
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              You&apos;re about to switch to the old form builder design. All saved data will be preserved and you may switch back anytime.
            </DialogContentText>
            <Typography sx={{ fontWeight: 600 }}>
              <Box component="span" sx={{ color: '#CE2C31', verticalAlign: 'super', fontSize: '16px' }}>
                * {' '}
              </Box>
              What didn&apos;t work for you?
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={switchReason}
              onChange={e => setSwitchReason(e.target.value)}
              required
            />
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleCloseSwitchDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmSwitch}
                disabled={!switchReason.trim()}
              >
                Switch version
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        {useV2Editor ? (
          <EditQuestionsIframe
            ref={iframeRef}
            formId={form.id}
            editQuestionIframeSource={editQuestionIframeSource}
            setCanPublishForm={setCanPublishForm}
            onVersionUpdate={onIframeVersionUpdate}
            onSwitchToV1={enforceV2Editor ? undefined : () => toggleEditorVersion('v1')}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Form
              form={formState}
              formErrors={formErrors}
              selectedElementID={selectedElementID}
              selectedElementRefCallback={selectedElementRefCallback}
              onSelectElement={setSelectedElementID}
              onReorderElement={onReorderElement}
              viewOnly={viewOnly}
            />

            <Editor
              form={formState}
              formErrors={formErrors}
              metadata={metadata}
              onAddMetadata={onAddMetadata}
              metadataValuesWithHeaderUrl={metadataValuesWithHeaderUrl}
              hasAdditionalMetadata={hasAdditionalMetadata}
              viewOnly={viewOnly}
              updateFormImageUrl={updateFormImageUrl}
              selectedElementID={selectedElementID}
              onChangeTitle={onChangeTitle}
              onChangeInstructions={onChangeInstructions}
              onUpdateElement={onUpdateElement}
              onCloneElement={onCloneElement}
              onDeleteElement={onDeleteElement}
              onNewElement={onNewElement}
              template={templateUsageInstructions}
            />
          </Stack>
        )}
      </main>

      {shouldShowTooltip() && !isMobileOrTablet && (
        <CustomTooltipAsPopover
          arrow={{
            direction: 'up',
            placement: 'center'
          }}
          popoverProps={{
            id: tooltipId,
            open: tooltipOpen,
            disablePortal: true,
            anchorEl: tooltipAnchorEl,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          }}
          onClose={onCloseTooltip}
          title="Scheduling options"
          bodyText="Now you can schedule your form to publish or unpublish at a later date."
        >
          <DefaultTooltipButton onButtonClick={onCloseTooltip} />
        </CustomTooltipAsPopover>
      )}

      {showNewUIDialog && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            maxWidth: 425,
            p: 3,
            borderRadius: 3.5
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
            <AutoAwesomeIcon sx={{ fontSize: '24px' }} />
            <Typography
              sx={{
                fontSize: '23px',
                lineHeight: '28px',
                fontWeight: 500
              }}
            >
              Build questions faster with our new Questions tab!
            </Typography>
          </Box>
          <Typography>
            <ul style={{ paddingLeft: '20px' }}>
              <li><Typography fontWeight={500}>One focused panel</Typography></li>
              <li><span style={{ fontWeight: 500 }}>Add new questions</span> above or below by clicking an existing question</li>
              <li><Typography fontWeight={500}>Easier drag-and-drop reordering</Typography></li>
              <li><span style={{ fontWeight: 500 }}>Smart pasting</span> multiple options into the first field and they&apos;re split automatically</li>
            </ul>
            Stay tuned for more exciting new features, such as live preview coming your way soon.
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="contained" onClick={handleCloseNewUIDialog}>
              Got it
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );

  function renderHeader() {
    return (
      <header className="manage-forms-edit-page-content-header">
        <Breadcrumbs className="breadcrumbs">
          <Link underline="hover" color="inherit" href={formsUrl}>
            Home
          </Link>
          <Typography>Questions</Typography>
        </Breadcrumbs>

        {renderSaveStatus()}
        {!enforceV2Editor && editQuestionIframeSource !== '' && (
          <Button className="action-button" variant="outlined" onClick={handleVersionSwitch} sx={{ gap: 1 }}>
            {useV2Editor ? <AutorenewIcon sx={{ height: '16px' }} /> : <AutoAwesomeIcon sx={{ height: '18px' }} />}
            {useV2Editor ? 'Switch to old version' : 'Try the new design'}
          </Button>
        )}
        <Button
          className="action-button"
          variant="outlined"
          href={previewUrl}
          target="_blank"
        >
          PREVIEW
        </Button>
        <Button
          ref={setTooltipAnchorEl}
          className="action-button"
          variant="contained"
          onClick={handleOpenShareModal}
          disabled={viewOnly || (useV2Editor && !canPublishForm)}
        >
          PUBLISH & SHARE
        </Button>
      </header>
    );
  }

  function renderSaveStatus() {
    if (viewOnly) {
      return (
        <Alert className="save-status" severity="warning">
          View Only
        </Alert>
      );
    }

    if (useV2Editor) {
      return null;
    }

    switch (saveStatus) {
      case SaveStatus.Saved: {
        return (
          <Alert className="save-status" severity="success">
            Saved
          </Alert>
        );
      }
      case SaveStatus.Saving: {
        return (
          <Alert className="save-status" severity="info">
            Saving...
          </Alert>
        );
      }
      case SaveStatus.Error: {
        const defaultMsg = 'Please fix error(s) to save';
        return (
          <Alert className="save-status" severity="error">
            {isEmpty(formErrors.version) ? defaultMsg : formErrors.version}
          </Alert>
        );
      }
      default: {
        return null;
      }
    }
  }

  function shouldShowTooltip(): boolean {
    return getLocalStorageItem(TOOLTIP_ID) !== '1';
  }

  function onCloseTooltip(): void {
    setTooltipAnchorEl(null);
    setTooltipAsShown();
  }

  function onChangeTitle(title: string) {
    setFormState(prevState => ({ ...prevState, title }));
  }

  function onChangeInstructions(instructions: string) {
    setFormState(prevState => ({ ...prevState, instructions }));
  }

  function onUpdateElement(element: UnionElement) {
    setFormState(prevState => {
      const newRoot = updateElement(prevState.body, element);
      return { ...prevState, body: newRoot };
    });
  }

  function onCloneElement(element: UnionElement) {
    setFormState(prevState => {
      const { newRoot, clone } = cloneElement(prevState.body, element);

      if (isNotEmpty(clone)) {
        setSelectedElementID(clone.id);
      }
      return { ...prevState, body: newRoot };
    });
  }

  function onDeleteElement(elementID: string) {
    setFormState(prevState => {
      setSelectedElementID(null);

      const newRoot = deleteElement(prevState.body, elementID);
      return { ...prevState, body: newRoot };
    });
  }

  function onNewElement(elementType: string) {
    setFormState(prevState => {
      const root = prevState.body;

      let insertParentElement: UnionElement;
      let insertIndex: number;

      if (isEmpty(selectedElementID)) {
        insertParentElement = root;
        insertIndex = root.children.length; // add to end of root
      } else if (
        selectedElementID === TitleID
        || selectedElementID === InstructionsID
      ) {
        insertParentElement = root;

        // if it is a locked template, then any new elements should be added after all the locked questions
        const isLockedTemplate = root.children.some(c => c.locked);

        if (isLockedTemplate) {
          const lastLockedIndex = findLastIndex(root.children, e => e.locked);
          // findLastIndex returns -1 if not found which will result in a valid insertIndex below
          insertIndex = lastLockedIndex + 1;
        } else {
          insertIndex = 0; // add to front of root
        }
      } else {
        /* truth table
         *
         * |                              | parent of selected is a section | selected is a section  | selected is not a section
         * |------------------------------|---------------------------------|------------------------|-------------------------
         * | new element is a section     | add after parent of selected    | add after selected     | add after selected
         * | new element is not a section | add after selected (in section) | add to end of selected | add after selected
         */

        const selectedElement = findElement(root, selectedElementID);
        const selectedElementParent = parentElementOf(root, selectedElementID);

        if (
          elementType === SectionElementType &&
          selectedElementParent.type === SectionElementType
        ) {
          // _parent_ of selected element and new element are sections
          insertParentElement = root;
          insertIndex = insertParentElement.children.indexOf(selectedElementParent) + 1; // add after _parent_ of selected element
        } else if (
          elementType === SectionElementType &&
          selectedElement.type === SectionElementType
        ) {
          // selected element and new element are sections
          insertParentElement = root;
          insertIndex = insertParentElement.children.indexOf(selectedElement) + 1; // add after selected element
        } else if (selectedElement.type === SectionElementType) {
          // selected element is a section but new element is _not_ a section
          insertParentElement = selectedElement;
          insertIndex = (selectedElement as SectionElement).children.length; // add to end of selected element
        } else {
          // selected element is not a section and new element can be anything, including a section
          insertParentElement = selectedElementParent;
          insertIndex = insertParentElement.children.indexOf(selectedElement) + 1; // add after selected element
        }
      }

      const elementToInsert = newElement(elementType);
      setSelectedElementID(elementToInsert.id);

      const newRoot = insertElement(
        root,
        insertParentElement.id,
        insertIndex,
        elementToInsert
      );
      return { ...prevState, body: newRoot };
    });
  }

  function onReorderElement(
    elementID: string,
    sourceParentID: string,
    sourceIndex: number,
    targetParentID: string,
    targetIndex: number
  ) {
    setFormState(prevState => {
      const newRoot = reorderElement(
        prevState.body,
        elementID,
        sourceParentID,
        sourceIndex,
        targetParentID,
        targetIndex
      );
      return { ...prevState, body: newRoot };
    });
  }
}

function setTooltipAsShown(): void {
  setLocalStorageItem(TOOLTIP_ID, '1');
}
